import { Button, Text } from "@ttc3k/trekker";
import { format, Locale, parse } from "date-fns";
import { NavArrowDown, NavArrowUp } from "iconoir-react";
import { useState } from "react";
import { EVENT_DATE_FORMAT } from "shared/utils";
import { Box, Flex, Grid } from "styled-system/jsx";
import { DateRange, YearGroup } from "widgets/events/types";
import { useEventsWidgetContext } from "../EventsContext";
import { AnimatePresence, motion } from "framer-motion";

type YearWithMonthsProps = {
  yearGroup: YearGroup;
  locale: Locale | undefined;
  selectedMonth: string;
  handleMonthClick: (month: string) => void;
};

/**
 * A component that displays a year and a list of months with event counts
 * @param yearGroup - The year group to display
 * @param locale - The locale to use
 * @param selectedMonth - The selected month
 * @param handleMonthClick - The function to call when a month is clicked
 * @returns A component that displays a year and a list of months with event counts
 */
const YearWithMonths = ({
  yearGroup,
  locale,
  selectedMonth,
  handleMonthClick,
}: YearWithMonthsProps) => {
  return (
    <Flex flexDir="column" gap="100">
      <Text visual="smallMedium">{yearGroup.year}</Text>
      <Grid gridRowGap={"9px"} gridColumnGap={"9px"} gridTemplateColumns={2}>
        {yearGroup.monthsAndEventCounts.map((monthAndEventCount) => (
          <Button
            key={monthAndEventCount.month}
            part="date__btn-month-mobile"
            visual="primary"
            mode="light"
            disabled={monthAndEventCount.eventCount === 0}
            onClick={() => handleMonthClick(monthAndEventCount.month)}
            flexGrow={1}
            px="150"
            py="100"
            display="flex"
            flexDir="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Text
              part="date__txt-month-mobile-month"
              visual="smallMedium"
              color={
                selectedMonth === monthAndEventCount.month
                  ? "text.accent.blue.mid"
                  : "text.dark"
              }
            >
              {format(
                parse(monthAndEventCount.month, EVENT_DATE_FORMAT, new Date()),
                "MMM",
                { locale },
              )}
            </Text>
            <Text
              part="date__txt-month-mobile-count"
              visual="smallRegular"
              color="text.light"
            >
              {monthAndEventCount.eventCount}
            </Text>
          </Button>
        ))}
      </Grid>
    </Flex>
  );
};

type MonthYearSelectorMobileProps = {
  yearGroups: YearGroup[];
  dateRange: DateRange;
  selectedMonth: string;
  handleMonthClick: (month: string) => void;
  setSelectedYear: (year: string) => void;
};

export const MonthYearSelectorMobile = ({
  yearGroups,
  dateRange,
  selectedMonth,
  handleMonthClick,
  setSelectedYear,
}: MonthYearSelectorMobileProps) => {
  const { locale } = useEventsWidgetContext();
  const [isOpen, setIsOpen] = useState(false);

  if (yearGroups.length === 0 || !dateRange || !selectedMonth) {
    return null;
  }

  const selectedMonthYear = format(
    parse(selectedMonth, EVENT_DATE_FORMAT, new Date()),
    "MMMM yyyy",
    { locale },
  );

  /**
   * Handle clicking on a month and closing the modal
   * @param month - The month to select
   */
  const handleMonthClickAndClose = (month: string) => {
    // We update the year here in case the screen is stretched to desktop size
    // This ensures the display is open to the correct year
    setSelectedYear(month.substring(0, 4));
    handleMonthClick(month);
    setIsOpen(false);
  };

  return (
    <Box position="relative" width="100%" overflow="visible">
      <Button
        part="date__btn-month-year"
        visual="unstyled"
        mode="light"
        color="text.dark"
        onClick={() => setIsOpen(!isOpen)}
        Icon={isOpen ? NavArrowUp : NavArrowDown}
        isIconRightAligned={true}
      >
        {selectedMonthYear}
      </Button>

      <AnimatePresence mode="wait">
        {isOpen && (
          <>
            <Box
              position="fixed"
              bottom="0"
              left="0"
              right="0"
              top="0"
              zIndex="max"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: "absolute",
                top: "38px",
                left: "0",
                right: "0",
                // This needs to be the highest z-index possible
                // because the date range selector has a z-index of 2147483646
                // so we need to be higher than that
                // Also, this needs to be the actual value because it is a style
                // and not a Panda className (using max is not translated to the number value)
                zIndex: "2147483647",
              }}
            >
              <Flex
                part="date__container-month-year-mobile"
                flexDir="column"
                gap="200"
                bg="white"
                borderRadius="8px"
                borderWidth="1px"
                borderColor="border.light"
                p="20px"
                boxShadow="0px 10px 15px -5px rgba(10, 10, 10, 0.10), 0px 5px 5px -2px rgba(10, 10, 10, 0.05);"
                w="272px"
              >
                {yearGroups.map((yearGroup) => (
                  <YearWithMonths
                    key={yearGroup.year}
                    yearGroup={yearGroup}
                    locale={locale}
                    selectedMonth={selectedMonth}
                    handleMonthClick={handleMonthClickAndClose}
                  />
                ))}
              </Flex>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </Box>
  );
};
